<template>
    <el-dialog
        ref="DetailDialog"
        class="financial-analysis-dialog ah-detail-dialog"
        top="10vh"
        append-to-body
        :visible="visible"
        :modal-append-to-body="false"
        @open="onOpen"
        @close="onClose"
    >
        <template slot="title">財務收支子項目分析</template>
        <div class="dialog-body">
            <div class="dialog-header">
                <div class="name">{{ year }} 年 {{ itemName }}</div>
            </div>
            <div>
                <v-chart :key="chartKey" :option="chartOption" autoresize style="width: 100%; height: 200px; margin: 20px 0" />
            </div>
            <div>
                <el-table
                    border
                    :data="tableData"
                    show-summary
                    :summary-method="getSummaries"
                    v-loading="isLoading"
                    size="mini"
                    style="width: 100%"
                >
                    <!-- 子項目名稱 -->
                    <el-table-column label="子項目" fixed min-width="200">
                        <template slot-scope="{ row, $index }">
                            <div class="item-name-set">
                                <span v-if="seriesColors[$index]" class="color-indicator" :style="{ backgroundColor: seriesColors[$index] }" />
                                <span v-if="row.unt === 'C_IN_ELS'" class="type-label income">收入</span>
                                <span v-if="row.unt === 'C_OUT_ELS'" class="type-label expense">支出</span>
                                <span class="item-name" @click="showListByYear(row.sno)">{{ row.name }}</span>
                            </div>
                        </template>
                    </el-table-column>

                    <!-- 1~12 月 -->
                    <el-table-column v-for="(month, index) in months" :key="month" :label="`${month}月`" align="right">
                        <template slot-scope="{ row }">
                            <span @click="showListByMonth(row.sno, month)" class="month-amt">
                                <money :v="row.monthlyTotals[index]" />
                            </span>
                        </template>
                    </el-table-column>

                    <!-- 合計 -->
                    <el-table-column prop="total" label="合計" align="right">
                        <template slot-scope="{ row }">
                            <span @click="showListByYear(row.sno)" class="month-amt">
                                <money :v="row.total" />
                            </span>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
            <div style="padding: 20px 0"></div>
        </div>
        <ItemListDialog
            :visible="listDialog.visible"
            :sno="listDialog.sno"
            :startDate="listDialog.startDate"
            :endDate="listDialog.endDate"
            @close="listDialog.visible = false"
        />
    </el-dialog>
</template>
<script>
import VChart from 'vue-echarts';
import ItemListDialog from './ItemListDialog.vue';
import moment from 'moment';

export default {
    components: {
        VChart,
        ItemListDialog,
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        year: {
            type: Number,
            default: null,
        },
        sno: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            itemName: '',
            months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            tableData: [],
            chartOption: {},
            seriesColors: [],
            summary: {
                monthlyTotals: [],
                grandTotal: 0,
            },
            listDialog: {
                visible: false,
                sno: null,
                startDate: null,
                endDate: null,
            },
            chartKey: 0,
        };
    },
    methods: {
        onOpen() {
            this.fetchData();
        },
        onClose() {
            this.$emit('close');
        },
        async fetchData() {
            this.isLoading = true;

            if (!this.sno) {
                this.$message.error('無編號(sno)');
                this.isLoading = false;
                return;
            }
            if (!this.year) {
                this.$message.error('無指定年份(year)');
                this.isLoading = false;
                return;
            }

            const resp = await this.$api({
                url: 'FinancialStatement/GetItemAnalysis',
                method: 'GET',
                params: {
                    sno: this.sno,
                    year: this.year,
                },
            });

            if (resp && resp.data) {
                this.itemName = resp.data.name;
                if (resp.data.items) {
                    this.tableData = resp.data.items;
                }
                if (resp.data.summary) {
                    this.summary.monthlyTotals = resp.data.summary.monthlyTotals;
                    this.summary.total = resp.data.summary.total;
                }
            } else {
                this.$message.error('無有效資料');
            }

            this.buildChartOption();
            this.isLoading = false;
        },
        // 合計列
        getSummaries({ columns }) {
            const sums = [];

            columns.forEach((column, index) => {
                // 第一欄顯示文字
                if (index === 0) {
                    sums[index] = '總計';
                    return;
                }

                // 月份欄位
                if (index > 0 && index <= this.months.length) {
                    const monthIndex = index - 1;
                    sums[index] = parseInt(this.summary.monthlyTotals[monthIndex], 10).toLocaleString();
                    return;
                }

                // 合計欄
                if (column.label === '合計') {
                    sums[index] = parseInt(this.summary.grandTotal, 10).toLocaleString();
                    return;
                }

                // % 欄不需要合計
                sums[index] = '';
            });

            return sums;
        },
        buildChartOption() {
            // 先清空图表，强制重新渲染
            this.chartOption = {};
            this.chartKey += 1;

            // 當沒有資料時，清空圖表
            if (!this.tableData || this.tableData.length === 0) {
                this.chartOption = {
                    tooltip: {
                        trigger: 'axis',
                    },
                    legend: {
                        top: 0,
                        data: [],
                    },
                    grid: {
                        left: '3%',
                        right: '3%',
                        top: 70,
                        bottom: 20,
                        containLabel: true,
                    },
                    xAxis: {
                        type: 'category',
                        boundaryGap: false,
                        data: this.months.map((m) => `${m}月`),
                    },
                    yAxis: {
                        type: 'value',
                        axisLine: {
                            show: false,
                        },
                        splitLine: {
                            lineStyle: {
                                color: 'rgba(0,0,0,0.1)',
                                width: 1,
                                type: 'solid',
                            },
                        },
                        axisLabel: {
                            formatter: (value) => value.toLocaleString(),
                        },
                    },
                    series: [],
                };
                return;
            }

            const legendData = this.tableData.map((item) => item.name);

            // ECharts 默认颜色
            const colors = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'];

            const series = this.tableData.map((item, index) => ({
                name: item.name,
                type: 'line',
                data: item.monthlyTotals,
                symbol: 'circle',
                symbolSize: 6,
                itemStyle: {
                    color: colors[index % colors.length],
                },
                lineStyle: {
                    color: colors[index % colors.length],
                },
                emphasis: {
                    focus: 'series',
                },
            }));

            // 保存颜色信息供表格使用
            this.seriesColors = this.tableData.map((_, index) => colors[index % colors.length]);

            this.chartOption = {
                tooltip: {
                    trigger: 'axis',
                    valueFormatter: (value) => (value ? value.toLocaleString() : '0'),
                },
                legend: {
                    top: 0,
                    data: legendData,
                },
                grid: {
                    left: '3%',
                    right: '3%',
                    top: 70,
                    bottom: 20,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: this.months.map((m) => `${m}月`),
                },
                yAxis: {
                    type: 'value',
                    axisLine: {
                        show: false,
                    },
                    splitLine: {
                        lineStyle: {
                            color: 'rgba(0,0,0,0.1)',
                            width: 1,
                            type: 'solid',
                        },
                    },
                    axisLabel: {
                        formatter: (value) => value.toLocaleString(),
                    },
                },
                series,
            };
        },
        showListByMonth(sno, month) {
            this.listDialog.visible = true;
            this.listDialog.sno = sno;
            this.listDialog.startDate = moment(`${this.year}-${month}-01`).startOf('month').format('x');
            this.listDialog.endDate = moment(`${this.year}-${month}-01`).endOf('month').format('x');
        },
        showListByYear(sno) {
            this.listDialog.visible = true;
            this.listDialog.sno = sno;
            this.listDialog.startDate = moment(`${this.year}-01-01`).startOf('year').format('x');
            this.listDialog.endDate = moment(`${this.year}-12-31`).endOf('year').format('x');
        },
    },
};
</script>
<style lang="scss">
.financial-analysis-dialog {
    .el-dialog {
        max-width: 1350px;
    }
    .dialog-body {
        padding: 20px;
    }
    .dialog-header {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: 15px 10px 0 0;
        gap: 15px;
        font-size: 13pt;
        font-weight: bold;
    }
    .color-indicator {
        display: inline-block;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        margin-right: 6px;
        vertical-align: middle;
    }
    .item-table.el-table {
        th, td {
            padding: 5px 0;
        }
    }

    .item-name-set {
        display: flex;
        align-items: center;
        gap: 5px;

        .item-name {
            cursor: pointer;

            &:hover {
                color: #463cb2;
                text-decoration: underline;
            }
        }

        .type-label {
            display: inline-block;
            background-color: #eee;
            padding: 4px 6px;
            border-radius: 5px;
            line-height: 100%;
            white-space: nowrap;

            &.income {
                background-color: rgb(178, 238, 172);
            }
            &.expense {
                background-color: rgb(238, 172, 172);
            }
        }
    }
    .month-amt {
            white-space: nowrap;
            cursor: pointer;
            &:hover > span {
                color: #463cb2;
                text-decoration: underline;
            }
        }
}

</style>
